// Decision-point builders (spec §9). Pure: they read the state and return a Decision with honest consequences.
import type { Controls, SimState, Decision, DecisionOption, AirEnv, SeaEnv, WeatherCell, SeaSegment } from '../types';
import { AIR_LAYERS_FT } from '../types';
import type { ResolvedCraft } from '../physics/resolve';
import { isJet, allowedMixtures } from '../physics/resolve';
import { cruisePoint, climbPoint } from '../physics/aircraft';
import { vesselPoint } from '../physics/vessel';
import { ftToM } from '../physics/atmosphere';

const pct = (ratio: number): string => `${ratio >= 1 ? '+' : '−'}${Math.abs((ratio - 1) * 100).toFixed(1)} %`;
const kFt = (ft: number): string => `${Math.round(ft).toLocaleString('en-US')} ft`;
const minutes = (s: number): string => `${Math.max(1, Math.round(s / 60))} min`;

function airSr(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv, altFt: number): { sr: number; tas: number; ok: boolean } {
  const pt = cruisePoint(rc, s.massKg, ftToM(altFt), c, env, s.storesJettisoned);
  return { sr: pt.srNmPerKg, tas: pt.vMs / 0.514444, ok: pt.ok };
}

/** "Climb to X ft: +3 % nm per kg, about 9 min and 420 kg" — consequence for an altitude change. */
function altitudeOption(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv, altFt: number, id: string): DecisionOption | null {
  const here = airSr(rc, s, c, env, s.altitudeFt);
  const there = airSr(rc, s, c, env, altFt);
  if (!there.ok || here.sr <= 0) return null;
  let cost = '';
  if (altFt > s.altitudeFt) {
    const cp = climbPoint(rc, s.massKg, ftToM(s.altitudeFt), c, s.storesJettisoned);
    if (cp.rocMs <= 0.5) return null;
    const climbS = (ftToM(altFt - s.altitudeFt)) / cp.rocMs;
    cost = `, about ${minutes(climbS)} and ${Math.round((cp.fuelKgPerH * climbS) / 3600)} kg to climb`;
  }
  const verb = altFt > s.altitudeFt ? 'Climb' : 'Descend';
  return {
    id,
    label: `${verb} to ${kFt(altFt)}`,
    consequence: `${Math.round(there.tas)} kt, ${pct(there.sr / here.sr)} nm per kg${cost}`,
    controls: { altitudeFt: altFt },
  };
}

export function topOfClimbDecision(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv): Decision {
  const here = airSr(rc, s, c, env, s.altitudeFt);
  const options: DecisionOption[] = [{ id: 'hold', label: 'Hold the plan', consequence: `${Math.round(here.tas)} kt, ${here.sr.toFixed(2)} nm per kg at ${kFt(s.altitudeFt)}` }];
  if (isJet(rc)) {
    for (const thrust of [0.6, 0.8, 1.0]) {
      if (Math.abs(thrust - (c.thrust ?? 0.6)) < 0.01) continue;
      const pt = cruisePoint(rc, s.massKg, ftToM(s.altitudeFt), { ...c, thrust }, env, s.storesJettisoned);
      if (!pt.ok || here.sr <= 0) continue;
      options.push({ id: `thrust-${thrust}`, label: `${Math.round(thrust * 100)} % thrust`, consequence: `${Math.round(pt.vMs / 0.514444)} kt, ${pct(pt.srNmPerKg / here.sr)} nm per kg`, controls: { thrust } });
    }
  } else {
    for (const power of [0.55, 0.65, 0.75]) {
      if (Math.abs(power - (c.power ?? 0.65)) < 0.01) continue;
      const allowed = allowedMixtures(rc, power);
      const mixture = allowed.includes(c.mixture ?? 'rich') ? c.mixture : allowed[allowed.length - 1];
      const pt = cruisePoint(rc, s.massKg, ftToM(s.altitudeFt), { ...c, power, mixture }, env, s.storesJettisoned);
      if (!pt.ok || here.sr <= 0) continue;
      options.push({ id: `power-${power}`, label: `${Math.round(power * 100)} % power`, consequence: `${Math.round(pt.vMs / 0.514444)} kt, ${pct(pt.srNmPerKg / here.sr)} nm per kg`, controls: { power, mixture } });
    }
  }
  return { kind: 'top-of-climb', title: 'Top of climb', detail: `Level at ${kFt(s.altitudeFt)} with ${Math.round(s.fuelKg)} kg of fuel. Confirm the cruise setting.`, options };
}

export function stepClimbDecision(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv, optFt: number): Decision | null {
  const climb = altitudeOption(rc, s, c, env, optFt, 'climb');
  if (!climb) return null;
  return {
    kind: 'step-climb',
    title: 'Step climb available',
    detail: `The aircraft is light enough that ${kFt(optFt)} gives more distance per kg than ${kFt(s.altitudeFt)}.`,
    options: [climb, { id: 'stay', label: `Stay at ${kFt(s.altitudeFt)}`, consequence: 'No change' }],
  };
}

/** Entering a segment whose best layer differs from the current altitude by ≥ 2,000 ft and ≥ 3 % specific range. */
export function airConditionsDecision(rc: ResolvedCraft, s: SimState, c: Controls, env: AirEnv, segmentNo: number): Decision | null {
  const here = airSr(rc, s, c, env, s.altitudeFt);
  if (here.sr <= 0) return null;
  let bestFt = s.altitudeFt, bestSr = here.sr;
  const top = rc.air ? rc.air.ceilingM / ftToM(1) : 0;
  for (const ft of AIR_LAYERS_FT) {
    if (ft > top || Math.abs(ft - s.altitudeFt) < 2000) continue;
    const there = airSr(rc, s, c, env, ft);
    if (there.ok && there.sr > bestSr) { bestFt = ft; bestSr = there.sr; }
  }
  if (bestFt === s.altitudeFt || bestSr < here.sr * 1.03) return null;
  const change = altitudeOption(rc, s, c, env, bestFt, 'change');
  if (!change) return null;
  const wind = env.windAlongKt(bestFt);
  return {
    kind: 'conditions-change',
    title: `Segment ${segmentNo}: wind favours ${kFt(bestFt)}`,
    detail: `Forecast at ${kFt(bestFt)}: ${wind >= 0 ? 'tailwind' : 'headwind'} ${Math.abs(Math.round(wind))} kt; at ${kFt(s.altitudeFt)}: ${Math.round(env.windAlongKt(s.altitudeFt))} kt.`,
    options: [change, { id: 'stay', label: `Stay at ${kFt(s.altitudeFt)}`, consequence: `${Math.round(here.tas)} kt, ${here.sr.toFixed(2)} nm per kg` }],
  };
}

/** Entering a sea segment where a 1 kn speed change gains ≥ 3 % nm per tonne. */
export function seaConditionsDecision(rc: ResolvedCraft, s: SimState, c: Controls, seg: SeaSegment, env: SeaEnv, segmentNo: number): Decision | null {
  const sea = rc.sea;
  if (!sea) return null;
  const speed = c.speedKn ?? sea.designSpeedKn;
  const here = vesselPoint(rc, speed, s.fouling, env);
  if (here.nmPerTonne <= 0) return null;
  const options: DecisionOption[] = [];
  let best = here.nmPerTonne;
  for (const v of [speed - 1, speed + 1]) {
    if (v < sea.minSpeedKn || v > sea.maxSpeedKn) continue;
    const pt = vesselPoint(rc, v, s.fouling, env);
    best = Math.max(best, pt.nmPerTonne);
    options.push({ id: `speed-${v}`, label: `${v.toFixed(1)} kn through the water`, consequence: `${pt.groundKt.toFixed(1)} kn over ground, ${pct(pt.nmPerTonne / here.nmPerTonne)} nm per tonne`, controls: { speedKn: v } });
  }
  if (best < here.nmPerTonne * 1.03) return null;
  options.push({ id: 'hold', label: `Hold ${speed.toFixed(1)} kn`, consequence: `${here.groundKt.toFixed(1)} kn over ground, ${here.nmPerTonne.toFixed(0)} nm per tonne` });
  return {
    kind: 'conditions-change',
    title: `Segment ${segmentNo}: conditions changed`,
    detail: `Current ${seg.currentKn >= 0 ? '+' : ''}${seg.currentKn.toFixed(1)} kn along track, sea state ${seg.seaState}.`,
    options,
  };
}

export function weatherDecision(cell: WeatherCell, domain: 'air' | 'sea', segmentNo: number): Decision {
  const penalty = domain === 'air' ? 'drag' : 'resistance';
  return {
    kind: 'weather-cell',
    title: `Segment ${segmentNo}: weather cell ahead`,
    options: [
      { id: 'divert', label: 'Divert around it', consequence: `+${cell.divertNm} nm of track, no penalty` },
      { id: 'push', label: 'Push through', consequence: `+${Math.round(cell.pushPenalty * 100)} % ${penalty} for this segment, no extra distance` },
    ],
    detail: 'The forecast is the actual weather. Pick the cheaper of the two.',
  };
}

export function reserveDecision(s: SimState, capacityKg: number, domain: 'air' | 'sea'): Decision {
  const unit = domain === 'air' ? 'kg' : 't';
  const left = domain === 'air' ? Math.round(s.fuelKg) : (s.fuelKg / 1000).toFixed(1);
  const rate = s.fuelFlowKgPerH > 0 ? s.fuelKg / s.fuelFlowKgPerH : 0;
  return {
    kind: 'reserve',
    title: 'Reserve fuel reached',
    detail: `${left} ${unit} left (10 % of ${domain === 'air' ? Math.round(capacityKg) + ' kg' : (capacityKg / 1000).toFixed(1) + ' t'}), about ${(rate * 60).toFixed(0)} min at the current flow.`,
    options: [
      { id: 'continue', label: 'Continue to a dry tank', consequence: `About +${Math.round(rate * s.groundKt)} nm, no reserve on arrival` },
      { id: 'end-run', label: 'End the run now', consequence: `Land with ${left} ${unit} in reserve; the distance so far counts` },
    ],
  };
}
